import React, { useState, useCallback, useEffect, useRef } from 'react';
import { PlaceContext, DropPoint } from '@m4a3/api-client';
import { orderfulApi } from '../../services/api-instances';
import PlaceContextDropPoints from '../PlaceContextDropPoints/PlaceContextDropPoints';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import PlaceDropPointMap from './PlaceDropPointMap';

import { createPortal } from 'react-dom';
import * as L from 'leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { rerouteOrderThunk } from '../../actions/adminActions';
import { espooCellId, espooTestSiteCellId, kerryCellId } from '../../constants';
import { selectSelectedCellId } from 'selectors/ordersSelector';

interface PlaceDropPointSelectorProps {
  // Core functionality
  /** Initial eircode to load automatically */
  initialEircode?: string;
  /** Callback when a drop point is selected */
  onDropPointSelect?: (dropPointId: string, lat: number, lng: number, placeContext: PlaceContext) => void;
  
  // UI customization
  /** Component title */
  title?: string;
  /** Component subtitle */
  subtitle?: string;
  /** Additional CSS classes */
  className?: string;
  
  // View modes
  /** Compact 200px read-only view for embedding */
  isCompact?: boolean;
  /** Enable full management features (add, delete, edit) - only available in full view */
  enableManagement?: boolean;
  /** Enable expand button in compact view to open full management modal */
  enableModalExpansion?: boolean;

  // Optional route summary for compact mode
  /** Mission/route status label to display in the Route panel (e.g. PROPOSED) */
  routeStatus?: string;
  /** Label/code for the origin pad (e.g. KER_A) */
  originPadLabel?: string;
  /** Label/code for the destination pad (e.g. BLA_B) */
  destinationPadLabel?: string;
  /** Order ID */
  orderId?: string;
  /** Suggested drop point latitude and longitude */
  suggestedDropPointLatLon?: string;
}

const PlaceDropPointSelector = ({
  initialEircode = '',
  onDropPointSelect,
  title = 'Drop Point Selection',
  subtitle = 'Enter an Eircode to view available drop points',
  className = '',
  isCompact = false,
  enableManagement = false,
  enableModalExpansion = false,
  routeStatus,
  originPadLabel,
  destinationPadLabel,
  orderId,
  suggestedDropPointLatLon,
}: PlaceDropPointSelectorProps) => {
  const [eircode, setEircode] = useState(initialEircode);
  const [placeContext, setPlaceContext] = useState<PlaceContext | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSavingDropPoint, setIsSavingDropPoint] = useState(false);
  const [isSettingPreferred, setIsSettingPreferred] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDropPointId, setSelectedDropPointId] = useState<string | null>(null);
  const [riskAssessment, setRiskAssessment] = useState<any>(null);
  const [isLoadingRiskAssessment, setIsLoadingRiskAssessment] = useState(false);
  const cellId = useSelector(selectSelectedCellId);
  
  // Management features state (only used when showManagement is true)
  const [clickMode, setClickMode] = useState<'select' | 'add-drop-point'>('select');
  const [newPinLocation, setNewPinLocation] = useState<L.LatLng | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(null);
  const [showDeletedPoints, setShowDeletedPoints] = useState(false);
  const dispatch = useDispatch();
  
  // Track if we've already auto-triggered pin-adding mode for no preferred drop point
  const hasAutoTriggeredAddPin = useRef(false);
  
  // Derived state - simplified logic
  const showSearchBox = !initialEircode;
  const showManagement = enableManagement && !isCompact; // Management features only available in full view
  // Note: isCompact automatically implies read-only behavior
  const isRestrictedCell = [espooCellId, espooTestSiteCellId].includes(Number(cellId));

  const handleEircodeSearch = useCallback(async (searchEircode: string) => {
    if (!searchEircode.trim()) {
      setError('Please enter a valid Eircode');
      return;
    }

    setIsSearching(true);
    setError(null);
    setPlaceContext(null);
    setSelectedDropPointId(null);

    try {
      const normalizedEircode = searchEircode.trim().toUpperCase();
      const context = await orderfulApi.getPlaceContextByEircode(normalizedEircode);
      setPlaceContext(context);
      setEircode(normalizedEircode); // Store confirmed, normalized eircode for use in callbacks
        
      // Handle both direct array and paginated response formats
      const dropPoints = context.dropPoints?.data || [];
      if (dropPoints.length === 0) {
        setError('No drop points found for this Eircode');
      } else {
        // Auto-select preferred drop point if it exists
        const preferredDropPointId = context.place.preferredDropPointId;
        if (preferredDropPointId && dropPoints.some(dp => dp.id === preferredDropPointId && !dp.isDeleted)) {
          setSelectedDropPointId(preferredDropPointId);
          
          // Trigger callback and risk assessment for preferred drop point
          const preferredDropPoint = dropPoints.find(dp => dp.id === preferredDropPointId);
          if (preferredDropPoint) {
            // Trigger risk assessment
            performRiskAssessment(preferredDropPoint.latitude, preferredDropPoint.longitude);
            
            // Trigger callback
            if (onDropPointSelect) {
              onDropPointSelect(preferredDropPointId, preferredDropPoint.latitude, preferredDropPoint.longitude, context);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching place context:', err);
      setError('Failed to fetch drop point data. Please check the Eircode and try again.');
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    handleEircodeSearch(eircode);
  }, [eircode, handleEircodeSearch]);

  // Auto-search when initialEircode is provided
  useEffect(() => {
    if (initialEircode && initialEircode.trim()) {
      handleEircodeSearch(initialEircode.trim());
    }
  }, [initialEircode, handleEircodeSearch]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
      }
    };
    
    if (isModalOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  // Risk evaluation function
  const performRiskAssessment = useCallback(async (latitude: number, longitude: number): Promise<void> => {
    setIsLoadingRiskAssessment(true);
    
    try {
      const riskResponse = await orderfulApi.evaluateDropPointRisk({
        latitude,
        longitude,
        radius: 3.0, // MAP_CONFIG.RISK_ASSESSMENT_RADIUS
        cellId: 7, // MAP_CONFIG.DEFAULT_CELL_ID
      });
      
      // Convert API response to RiskAssessment format
      const assessment = {
        groundRisk: riskResponse.heightData?.height !== null && riskResponse.heightData?.height >= 1.0 ? 'HIGH' : 'LOW',
        buildingRisk: riskResponse.isNearBuilding ? 'HIGH' : 'LOW',
        overallRisk: (riskResponse.isNearBuilding || (riskResponse.heightData?.height !== null && riskResponse.heightData?.height >= 1.0)) ? 'HIGH' : 'LOW',
        canSave: !riskResponse.isNearBuilding || (riskResponse.heightData?.height !== null && riskResponse.heightData?.height < 1.0),
        message: riskResponse.isNearBuilding ? 'Near building detected' : 'Clear area',
        heightData: riskResponse.heightData?.height || undefined,
        buildingReason: riskResponse.isNearBuilding ? 'Near building detected' : 'Clear of buildings',
        hasError: false
      };
      
      setRiskAssessment(assessment);
    } catch (error) {
      console.error('Risk assessment failed:', error);
      
      // Create error assessment
      const errorAssessment = {
        groundRisk: 'ERROR',
        buildingRisk: 'ERROR', 
        overallRisk: 'ERROR',
        canSave: false,
        message: 'Risk evaluation failed',
        hasError: true
      };
      
      setRiskAssessment(errorAssessment);
    } finally {
      setIsLoadingRiskAssessment(false);
    }
  }, []);

  const handleDropPointSelect = useCallback((dropPointId: string, lat: number, lng: number) => {
    setSelectedDropPointId(dropPointId);
    
    // Trigger risk assessment for selected drop point
    performRiskAssessment(lat, lng);
    
    if (onDropPointSelect && placeContext) {
      onDropPointSelect(dropPointId, lat, lng, placeContext);
    }
  }, [onDropPointSelect, placeContext, performRiskAssessment]);

  // Management feature handlers
  const handleAddPin = useCallback(async () => {
    if (!placeContext) return;
    
    setClickMode('add-drop-point');
    setRiskAssessment(null);
    
    // Get place centroid coordinates from the place geometry
    let latitude, longitude;
    
    try {
      // Try to parse place geometry to get centroid
      const geometry = typeof placeContext.place.geometry === 'string' 
        ? JSON.parse(placeContext.place.geometry)
        : placeContext.place.geometry;
        
      if (geometry.type === 'Polygon' && geometry.coordinates && geometry.coordinates[0]) {
        const coords = geometry.coordinates[0];
        longitude = coords.reduce((sum: number, coord: number[]) => sum + coord[0], 0) / coords.length;
        latitude = coords.reduce((sum: number, coord: number[]) => sum + coord[1], 0) / coords.length;
      } else {
        throw new Error('Cannot extract coordinates from geometry');
      }
    } catch (error) {
      console.warn('Failed to get place centroid, using place centroid:', error);
      latitude = placeContext.place.centroid?.latitude || 53.3498;
      longitude = placeContext.place.centroid?.longitude || -6.2603;
    }
    
    // Place pin at calculated location
    const pinLocation = L.latLng(latitude, longitude);
    setNewPinLocation(pinLocation);
    
    // Start risk assessment for the initial pin location
    performRiskAssessment(latitude, longitude);
  }, [placeContext, performRiskAssessment]);

  // Auto-trigger pin-adding mode if no preferred drop point exists
  useEffect(() => {
    // Check all conditions for auto-triggering
    const shouldAutoTrigger = 
      !hasAutoTriggeredAddPin.current && // Haven't triggered yet
      placeContext !== null && // Place context is loaded
      !placeContext.place?.preferredDropPointId && // No preferred drop point
      placeContext.place?.enabled === true && // Place is enabled
      (showManagement || isModalOpen); // Management features are enabled OR modal is open
    
    if (shouldAutoTrigger) {
      hasAutoTriggeredAddPin.current = true;
      handleAddPin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeContext, isModalOpen]);

  const handleSavePin = useCallback(async () => {

    const isKerryCell = cellId === kerryCellId;
    if (!isKerryCell) {
      if (!placeContext || !newPinLocation || !riskAssessment) return;
      if (!riskAssessment.canSave) {
        setError('Cannot save drop point due to risk assessment');
        return;
      }
    }

    setIsSavingDropPoint(true);

    try {
      const newDropPoint = await orderfulApi.createDropPoint(
        placeContext.placeId,
        {
          latitude: newPinLocation.lat,
          longitude: newPinLocation.lng,
          verified: false,
        }
      );

      // Refresh place context to show new drop point
      const refreshedContext = await orderfulApi.getPlaceContextByEircode(eircode.trim().toUpperCase());
      setPlaceContext(refreshedContext);
      // Select the new drop point
      setSelectedDropPointId(newDropPoint.id);
      
      // Reset pin placement mode
      setClickMode('select');
      setNewPinLocation(null);
      setRiskAssessment(null);

      if (!placeContext?.place?.preferredDropPointId) {
        setPreferredDropPoint(newDropPoint);
      }
      
    } catch (err) {
      console.error('Error creating drop point:', err);
      setError('Failed to create drop point. Please try again.');
    } finally {
      setIsSavingDropPoint(false);
    }
  }, [placeContext, newPinLocation, riskAssessment, eircode]);

  const handleCancelPin = useCallback(() => {
    setClickMode('select');
    setNewPinLocation(null);
    setRiskAssessment(null);
  }, []);

  const handlePinMove = useCallback(async (newPosition: L.LatLng) => {
    setNewPinLocation(newPosition);
    
    // Perform risk assessment for the new position
    performRiskAssessment(newPosition.lat, newPosition.lng);
  }, [performRiskAssessment]);

  const handleDropPointDelete = useCallback(async (dropPoint: DropPoint) => {
    try {
      await orderfulApi.deleteDropPoint(dropPoint.id);
      
      // Refresh place context
      const refreshedContext = await orderfulApi.getPlaceContextByEircode(eircode.trim().toUpperCase());
      setPlaceContext(refreshedContext);
      
      // Clear selection if deleted drop point was selected
      if (selectedDropPointId === dropPoint.id) {
        setSelectedDropPointId(null);
        setRiskAssessment(null);
      }
      
      setShowConfirmDelete(null);

      // Trigger order reroute when a drop point is deleted
      if (orderId) {
        dispatch(rerouteOrderThunk(orderId));
      }
    } catch (err) {
      console.error('Error deleting drop point:', err);
      setError('Failed to delete drop point. Please try again.');
    } 
  }, [eircode, selectedDropPointId]);

  const setPreferredDropPoint = useCallback(async (dropPoint: DropPoint) => {

    if (!placeContext) return;
    // Disallow setting unverified as preferred in restricted cells
    if (isRestrictedCell && !dropPoint.verified) {
      setError('Cannot set unverified delivery spot as preferred in this cell');
      return;
    }

    setIsSettingPreferred(true);
    try {
      // Update place with preferred drop point ID using updatePlaceContext
      await orderfulApi.updatePlaceContext(placeContext.placeId, { 
        enabled: placeContext.place.enabled,
        preferredDropPointId: dropPoint.id 
      });
      
      // Refresh place context
      const refreshedContext = await orderfulApi.getPlaceContextByEircode(eircode.trim().toUpperCase());

      setPlaceContext(refreshedContext);

      dispatch(rerouteOrderThunk(orderId));
      
    } catch (err) {
      console.error('Error setting preferred delivery spot:', err);
      setError('Failed to set preferred delivery spot. Please try again.');
    } finally {
      setIsSettingPreferred(false);
    }
  }, [placeContext, eircode, isRestrictedCell]);

  const handleRemovePreferred = useCallback(async () => {
    if (!placeContext) return;

    setIsSettingPreferred(true);
    try {
      // Use any to allow null clearing for preferredDropPointId
      const payload: any = {
        enabled: placeContext.place.enabled,
        preferredDropPointId: null,
      };
      await orderfulApi.updatePlaceContext(placeContext.placeId, payload);

      // Refresh place context
      const refreshedContext = await orderfulApi.getPlaceContextByEircode(eircode.trim().toUpperCase());
      setPlaceContext(refreshedContext);

      dispatch(rerouteOrderThunk(orderId));
    } catch (err) {
      console.error('Error removing preferred drop point:', err);
      setError('Failed to remove preferred drop point. Please try again.');
    } finally {
      setIsSettingPreferred(false);
    }
  }, [placeContext, eircode]);

  const formatEircode = (value: string) => {
    // Remove any non-alphanumeric characters and convert to uppercase
    const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    
    // Irish Eircode format: 7 characters (e.g., D02X285)
    return cleaned.slice(0, 7);
  };

  const handleEircodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatEircode(e.target.value);
    setEircode(formatted);
  };

  const isValidEircode = (code: string) => {
    // Basic Eircode validation: 7 characters
    return code?.length === 7 && /^[A-Z0-9]{7}$/i.test(code);
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Header - only show if no initialEircode */}
      {showSearchBox && (
        <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4">
          <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
          <p className="text-sm text-gray-600 mb-3">{subtitle}</p>
          
          <form onSubmit={handleSubmit} className="space-y-2">
            <div>
              <label htmlFor="eircode-input" className="sr-only">
                Eircode
              </label>
              <input
                id="eircode-input"
                type="text"
                value={eircode}
                onChange={handleEircodeChange}
                placeholder="D02X285"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                disabled={isSearching}
                maxLength={7} // 7 characters
              />
            </div>
            <button
              type="submit"
              disabled={isSearching || !isValidEircode(eircode)}
              className="w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {isSearching ? (
                <div className="flex items-center justify-center">
                  <LoadingSpinner size={14} />
                  <span className="ml-2">Searching...</span>
                </div>
              ) : (
                'Search'
              )}
            </button>
          </form>

          {/* Validation feedback */}
          {eircode && !isValidEircode(eircode) && (
            <p className="text-xs text-orange-600 mt-2">
              Enter 7 characters (e.g., D02X285)
            </p>
          )}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-800 text-xs">{error}</p>
        </div>
      )}

      {/* Compact panels (rendered below the mini-map) are moved further down */}

      {/* Map Display */}
      {!isCompact && placeContext && (() => {
        const activeDropPoints = (placeContext.dropPoints?.data || []).filter(dp => !dp.isDeleted);
        const hasActiveDropPoints = activeDropPoints.length > 0;
        const showPinControls = showManagement && clickMode === 'add-drop-point';
        
        // Calculate map height based on active drop points and pin control visibility
        // Use viewport-based heights with minimum of 24rem
        let mapHeight = 'max(24rem, 35vh)'; // default - responsive with minimum
        if (showPinControls) {
          mapHeight = hasActiveDropPoints ? 'max(24rem, 40vh)' : 'max(24rem, 55vh)';
        } else {
          // When pin controls are hidden, expand map
          mapHeight = hasActiveDropPoints ? 'max(24rem, 40vh)' : 'max(24rem, 60vh)';
        }
        
        return (
          <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 relative" style={{ isolation: 'isolate' }}>
            {/* Map wrapper with toggle overlay */}
            <div className="relative">
              <PlaceDropPointMap
                placeContext={placeContext}
                selectedDropPointId={selectedDropPointId}
                onDropPointSelect={handleDropPointSelect}
                height={mapHeight}
                showLegend={false}
                suggestedDropPointLatLon={suggestedDropPointLatLon}
                clickMode={showManagement ? clickMode : 'select'}
                newPinLocation={showManagement ? newPinLocation : null}
                onPinMove={showManagement ? handlePinMove : undefined}
                riskAssessment={riskAssessment}
                isLoadingRiskAssessment={isLoadingRiskAssessment}
                showDeletedPoints={showManagement ? showDeletedPoints : false}
              />
            
            {/* Show Inactive Toggle - Bottom left of map */}
            {showManagement && (
              <div className="absolute bottom-3 left-3 z-[1000]">
                <label className="flex items-center text-xs text-gray-700 cursor-pointer bg-white px-3 py-2 rounded-lg shadow-md border border-gray-200">
                  <input
                    type="checkbox"
                    checked={showDeletedPoints}
                    onChange={(e) => setShowDeletedPoints(e.target.checked)}
                    className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Show inactive delivery spots
                </label>
              </div>
            )}
            
            {/* Add Drop Point Button - Bottom right of map */}
            {showManagement && clickMode !== 'add-drop-point' && (
              <div className="absolute bottom-3 right-3 z-[1000]">
                <button
                  onClick={handleAddPin}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-md"
                >
                  + Add Delivery Spot
                </button>
              </div>
            )}
          </div>
          
          {/* Pin Placement Controls - Below map */}
          {showManagement && clickMode === 'add-drop-point' && (
            <div className="bg-white p-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-gray-700 font-semibold">
                  Click on the map or drag the pin to place the new drop point
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleSavePin}
                    disabled={cellId !== kerryCellId && (isSavingDropPoint || !riskAssessment?.canSave)}
                    className="px-3 py-1 rounded text-xs text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:text-gray-400 disabled:hover:bg-gray-600 disabled:cursor-not-allowed"
                  >
                    {isSavingDropPoint ? 'Saving...' : (placeContext?.place?.preferredDropPointId ? 'Save Delivery Spot' : 'Set Preferred Delivery Spot')}
                  </button>
                  <button
                    onClick={handleCancelPin}
                    className="px-3 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
              
              {isLoadingRiskAssessment ? (
                <div className="flex items-center">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="animate-spin rounded-full h-3 w-3 border-b border-gray-600"></div>
                    <span>Evaluating risk...</span>
                  </div>
                </div>
              ) : riskAssessment && (
                <div>
                  <div className="text-xs text-gray-600 mb-1 font-semibold">Risk Assessment:</div>
                  <div className="flex items-center gap-4 text-xs">
                    <span className={`font-semibold ${
                      riskAssessment.buildingRisk === 'HIGH' ? 'text-red-600' : 
                      riskAssessment.buildingRisk === 'LOW' ? 'text-green-600' : 'text-gray-600'
                    }`}>
                      Building: {riskAssessment.buildingRisk === 'LOW' ? 'Clear' : 'Near building'}
                    </span>
                    <span className={`font-semibold ${
                      riskAssessment.groundRisk === 'HIGH' ? 'text-red-600' : 
                      riskAssessment.groundRisk === 'LOW' ? 'text-green-600' : 'text-gray-600'
                    }`}>
                      Ground: {typeof riskAssessment.heightData === 'number' 
                        ? `${riskAssessment.groundRisk === 'LOW' ? 'Safe' : 'HIGH'} (${riskAssessment.heightData.toFixed(1)}m)`
                        : riskAssessment.groundRisk}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        );
      })()}

      {/* Drop Points Display */}
      {!isCompact && placeContext && (() => {
        const activeDropPoints = (placeContext.dropPoints?.data || []).filter(dp => !dp.isDeleted);
        const hasActiveDropPoints = activeDropPoints.length > 0;
        
        if (!hasActiveDropPoints) {
          return null;
        }
        
        return (
          <PlaceContextDropPoints
            placeContext={placeContext}
            selectedDropPointId={selectedDropPointId}
            onDropPointSelect={handleDropPointSelect}
            title="Delivery Spots"
            subtitle="Select a delivery spot"
            isNarrow={false}
            riskAssessment={riskAssessment}
            isLoadingRiskAssessment={isLoadingRiskAssessment}
            showManagementFeatures={showManagement}
            onDropPointDelete={showManagement ? handleDropPointDelete : undefined}
            onSetPreferred={showManagement ? setPreferredDropPoint : undefined}
            onRemovePreferred={showManagement ? handleRemovePreferred : undefined}
            showConfirmDelete={showConfirmDelete}
            onShowConfirmDelete={setShowConfirmDelete}
            showDeletedPoints={showManagement ? showDeletedPoints : false}
            onAddPin={undefined}
            clickMode={clickMode}
            showDeletedPointsToggle={false}
            onShowDeletedPointsChange={showManagement ? setShowDeletedPoints : undefined}
            settingPreferredId={isSettingPreferred ? (selectedDropPointId || null) : null}
            isRestrictedCell={isRestrictedCell}
          />
        );
      })()}

      {!isCompact && placeContext && !showManagement && (
        (() => {
          const selectedDp = (placeContext.dropPoints?.data || []).find(dp => dp.id === selectedDropPointId);
          const isAlreadyPreferred = placeContext.place.preferredDropPointId === selectedDropPointId;
          const isRestrictedUnverified = !!selectedDp && isRestrictedCell && !selectedDp.verified;
          const isDisabled = isSettingPreferred || !selectedDp || (!!selectedDp && selectedDp.isDeleted) || isAlreadyPreferred || isRestrictedUnverified;
          return (
            <div className="mt-2 mb-4 px-6">
              <button
                onClick={() => selectedDp && setPreferredDropPoint(selectedDp)}
                disabled={isDisabled}
                className={`w-full px-3 py-2 rounded text-xs transition-colors ${
                  isDisabled
                    ? 'bg-purple-300 text-white cursor-not-allowed opacity-60'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
                title={isRestrictedUnverified ? 'Verify delivery spot before setting as preferred' : undefined}
              >
                {isAlreadyPreferred ? '★ Preferred drop point' : (isSettingPreferred ? 'Setting…' : 'Set as Preferred')}
              </button>
            </div>
          );
        })()
      )}

      {/* Internal Modal for Expansion */}
      {enableModalExpansion && isModalOpen && createPortal(
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 99999 }}>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setIsModalOpen(false)}
            style={{ zIndex: 99998 }}
          />
          
          {/* Modal */}
          <div className="flex items-center justify-center relative w-full" style={{ zIndex: 99999 }}>
            <div 
              className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
              style={{ zIndex: 100000, height: '80vh' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 rounded-full transition-colors"
                  title="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Content - Render the same component in management mode */}
              <div className="p-4 flex-1 overflow-y-scroll">
                {placeContext && (() => {
                  const activeDropPoints = (placeContext.dropPoints?.data || []).filter(dp => !dp.isDeleted);
                  const hasActiveDropPoints = activeDropPoints.length > 0;
                  const showPinControls = clickMode === 'add-drop-point';
                  
                  // Calculate map height based on active drop points and pin control visibility
                  // Use viewport-based heights with minimum of 24rem
                  let mapHeight = 'max(24rem, 35vh)'; // default - responsive with minimum
                  if (showPinControls) {
                    mapHeight = hasActiveDropPoints ? 'max(24rem, 40vh)' : 'max(24rem, 55vh)';
                  } else {
                    // When pin controls are hidden, expand map
                    mapHeight = hasActiveDropPoints ? 'max(24rem, 40vh)' : 'max(24rem, 60vh)';
                  }
                  
                  return (
                    <>
                      {/* Map Display */}
                      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 relative" style={{ isolation: 'isolate' }}>
                        {/* Map wrapper with toggle overlay */}
                        <div className="relative">
                          <PlaceDropPointMap
                            key={`modal-map-${placeContext.place.id}-${selectedDropPointId || 'none'}`}
                            placeContext={placeContext}
                            selectedDropPointId={selectedDropPointId}
                            onDropPointSelect={handleDropPointSelect}
                            height={mapHeight}
                            showLegend={false}
                            clickMode={clickMode}
                            newPinLocation={newPinLocation}
                            onPinMove={handlePinMove}
                            riskAssessment={riskAssessment}
                            isLoadingRiskAssessment={isLoadingRiskAssessment}
                            suggestedDropPointLatLon={suggestedDropPointLatLon}
                            showDeletedPoints={showDeletedPoints}
                          />
                        
                        {/* Show Inactive Toggle - Bottom left of map */}
                        <div className="absolute bottom-3 left-3 z-[1000]">
                          <label className="flex items-center text-xs text-gray-700 cursor-pointer bg-white px-3 py-2 rounded-lg shadow-md border border-gray-200">
                            <input
                              type="checkbox"
                              checked={showDeletedPoints}
                              onChange={(e) => setShowDeletedPoints(e.target.checked)}
                              className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            Show inactive delivery spots
                          </label>
                        </div>
                        
                        {/* Add Drop Point Button - Bottom right of map */}
                        {clickMode !== 'add-drop-point' && (
                        <div className="absolute bottom-3 right-3 z-[1000]">
                          <button
                            onClick={handleAddPin}
                              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-md"
                          >
                              + Add Delivery Spot
                          </button>
                        </div>
                        )}
                      </div>
                      
                      {/* Pin Placement Controls - Below map */}
                      {clickMode === 'add-drop-point' && (
                        <div className="bg-white p-4 mt-4">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs text-gray-700 font-semibold">
                              Click on the map or drag the pin to place the new drop point
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={handleSavePin}
                                disabled={cellId !== kerryCellId && (isSavingDropPoint || !riskAssessment?.canSave)}
                                className="px-3 py-1 rounded text-xs text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:text-gray-400 disabled:hover:bg-gray-600 disabled:cursor-not-allowed"
                              >
                                {isSavingDropPoint ? 'Saving...' : (placeContext?.place?.preferredDropPointId ? 'Save Delivery Spot' : 'Set Preferred Delivery Spot')}
                              </button>
                              <button
                                onClick={handleCancelPin}
                                className="px-3 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                          
                          {isLoadingRiskAssessment ? (
                            <div className="flex items-center">
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <div className="animate-spin rounded-full h-3 w-3 border-b border-gray-600"></div>
                                <span>Evaluating risk...</span>
                              </div>
                            </div>
                          ) : riskAssessment && (
                            <div>
                              <div className="text-xs text-gray-600 mb-1 font-semibold">Risk Assessment:</div>
                              <div className="flex items-center gap-4 text-xs">
                                <span className={`font-semibold ${
                                  riskAssessment.buildingRisk === 'HIGH' ? 'text-red-600' : 
                                  riskAssessment.buildingRisk === 'LOW' ? 'text-green-600' : 'text-gray-600'
                                }`}>
                                  Building: {riskAssessment.buildingRisk === 'LOW' ? 'Clear' : 'Near building'}
                                </span>
                                <span className={`font-semibold ${
                                  riskAssessment.groundRisk === 'HIGH' ? 'text-red-600' : 
                                  riskAssessment.groundRisk === 'LOW' ? 'text-green-600' : 'text-gray-600'
                                }`}>
                                  Ground: {typeof riskAssessment.heightData === 'number' 
                                    ? `${riskAssessment.groundRisk === 'LOW' ? 'Safe' : 'HIGH'} (${riskAssessment.heightData.toFixed(1)}m)`
                                    : riskAssessment.groundRisk}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                      {/* Drop Points Display */}
                      {hasActiveDropPoints && (
                        <PlaceContextDropPoints
                          placeContext={placeContext}
                          selectedDropPointId={selectedDropPointId}
                          onDropPointSelect={handleDropPointSelect}
                          title="Delivery Spots"
                          subtitle="Select a delivery spot"
                          isNarrow={false}
                          riskAssessment={riskAssessment}
                          isLoadingRiskAssessment={isLoadingRiskAssessment}
                          showManagementFeatures={true}
                          onDropPointDelete={handleDropPointDelete}
                          onSetPreferred={setPreferredDropPoint}
                          onRemovePreferred={handleRemovePreferred}
                          showConfirmDelete={showConfirmDelete}
                          onShowConfirmDelete={setShowConfirmDelete}
                          showDeletedPoints={showDeletedPoints}
                          onAddPin={undefined}
                          clickMode={clickMode}
                          showDeletedPointsToggle={false}
                          onShowDeletedPointsChange={setShowDeletedPoints}
                          settingPreferredId={isSettingPreferred ? (selectedDropPointId || null) : null}
                          isRestrictedCell={isRestrictedCell}
                        />
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PlaceDropPointSelector;
